import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatLocalizedDate } from "@/lib/localized-date";

interface InlineEditableFieldProps {
  label: string;
  value: string | number | boolean | null | undefined;
  fieldName: string;
  type?: "text" | "date" | "select" | "textarea" | "number" | "toggle";
  isEditing: boolean;
  canEdit: boolean;
  onChange: (fieldName: string, value: string) => void;
  options?: { value: string; label: string }[];
  icon?: React.ElementType;
}

export function InlineEditableField({
  label,
  value,
  fieldName,
  type = "text",
  isEditing,
  canEdit,
  onChange,
  options,
  icon: Icon,
}: InlineEditableFieldProps) {
  const { t, i18n } = useTranslation();
  const displayValue = typeof value === "boolean" ? (value ? "true" : "false") : (value != null && value !== "" ? String(value) : "");
  const readOnlyValue = type === "date" && displayValue
    ? formatLocalizedDate(displayValue, i18n.resolvedLanguage || i18n.language)
    : type === "select" && options
      ? options.find((option) => option.value === displayValue)?.label ?? displayValue
      : displayValue;
  const boolValue = typeof value === "boolean" ? value : displayValue === "true";

  if (type === "toggle") {
    if (isEditing && canEdit) {
      return (
        <div className="flex items-center gap-3 py-2.5">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
          <Switch checked={boolValue} onCheckedChange={(checked) => onChange(fieldName, String(checked))} />
        </div>
      );
    }
    return (
      <div className="flex items-start gap-3 py-2.5">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">{label}</p>
          <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${boolValue ? "bg-success/10 text-success border border-success/20" : "bg-muted text-muted-foreground"}`}>
            {boolValue ? t("common.yes") : t("common.no")}
          </span>
        </div>
      </div>
    );
  }

  if (isEditing && canEdit) {
    return (
      <div className="flex items-start gap-3 py-2.5">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-2.5 shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground mb-1">{label}</p>
          {type === "select" && options ? (
            <Select value={displayValue} onValueChange={(v) => onChange(fieldName, v)}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder={t("common.notSet")} />
              </SelectTrigger>
              <SelectContent>
                {options.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : type === "textarea" ? (
            <Textarea
              value={displayValue}
              onChange={(e) => onChange(fieldName, e.target.value)}
              className="min-h-[60px] text-sm"
            />
          ) : type === "number" ? (
            <Input
              type="number"
              value={displayValue}
              onChange={(e) => onChange(fieldName, e.target.value)}
              className="h-8 text-sm"
            />
          ) : type === "date" ? (
            <DatePicker
              value={displayValue}
              onChange={(v) => onChange(fieldName, v)}
              className="h-8 text-sm"
              disableFutureDates={/birthday|date_of_birth/i.test(fieldName)}
            />
          ) : (
            <Input
              type={type}
              value={displayValue}
              onChange={(e) => onChange(fieldName, e.target.value)}
              className="h-8 text-sm"
            />
          )}
        </div>
      </div>
    );
  }

  if (isEditing && !canEdit) {
    return (
      <div className="flex items-start gap-3 py-2.5">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">{label}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <p className="text-sm font-medium text-foreground">
              {readOnlyValue || t("common.notSet")}
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <Lock className="h-3 w-3 text-muted-foreground/50" />
              </TooltipTrigger>
              <TooltipContent>{t("profile.noEditRights")}</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 py-2.5">
      {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground mt-0.5">
          {readOnlyValue || <span className="text-muted-foreground italic">{t("common.notSet")}</span>}
        </p>
      </div>
    </div>
  );
}
